import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { Divider } from '@fs/zion-ui'
import { UserContext } from '../UserContext'
import DmcDataView from '../../common/DmcDataView'
import { MrnLink, PersonLink } from '../../app/Links'

const justBorder = css`
  border: 1px solid #d7d7d7;
  box-shadow: 0px 1px #bfbfbf;
  border-radius: 5px;
  margin: 0 0 10px 0;
`
const relationshipStyle = css`
  color: grey;
  font-size: 0.75rem;
  padding: 0 0.5em;
`
const relationshipFlexItem = css`
  width: 100%;
  padding-top: 0.5em;
  font-size: 0.9rem;
`
const factTypeStyle = css`
  font-size: 0.9rem;
  font-weight: 600;
  color: #3a3a3a;
  padding-inline-start: 1.25em;
`

const factContainer = css`
  margin-top: 0.5em;
  padding: 0.25em 0.5em;
  background-color: #fafafa;
  border-inline-start: 3px solid #e1e1e1;
  margin-inline-start: 1em;
`
const flexContainer = css`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  align-items: baseline;
  margin: 0 0 10px 0;
`

const FACTS_FIELD_NAMES = [
  { name: 'date', label: 'Date' },
  { name: 'place', label: 'Place' },
  { name: 'value', label: 'Value' },
]

const RELATIONSHIP_TYPES = [
  { type: 'PARENT', female: 'Mother', male: 'Father', unknown: 'Parent' },
  { type: 'CHILD', female: 'Daughter', male: 'Son', unknown: 'Child' },
  { type: 'SPOUSE', female: 'Wife', male: 'Husband', unknown: 'Spouse' },
]

const getFactData = (fact) =>
  FACTS_FIELD_NAMES.map((field) => ({
    label: field.label,
    detail: fact[field.name],
    key: `${fact.type}-${field.name}`,
  })).filter((row) => row.detail)

const Facts = ({ factList }) => {
  if (factList == null) return null
  return factList.map((fact) => (
    <div key={`${fact.type}-${fact.date}-${fact.place}-${fact.value}`} css={factContainer}>
      <div data-testid={fact.type} css={factTypeStyle}>
        {fact.type}
      </div>
      <DmcDataView id={fact.type} data={getFactData(fact)} />
    </div>
  ))
}

const MrnRelationship = ({ relative, lookupMrn, relationshipType, mrnLookupList }) => {
  const relationType = RELATIONSHIP_TYPES.find((rel) => rel.type === relationshipType)
  const person = lookupMrn && mrnLookupList?.find((mrn) => mrn.mrn === lookupMrn)
  const relationship = person?.gender && relationType[person.gender.toLowerCase()]
  const personId = person?.personId

  return (
    <>
      <span data-testid={relationship || relationType.unknown} css={relationshipStyle}>
        {relationship || relationType.unknown}:{' '}
      </span>
      {person?.fullName || relative?.name || 'No Name Available'}
      {lookupMrn && (
        <>
          <span> - MRN:</span> <MrnLink mrn={lookupMrn} mrnText={`${lookupMrn}`} newTab />
        </>
      )}
      {personId && (
        <>
          <span> - PID:</span> <PersonLink personId={personId} />
        </>
      )}
    </>
  )
}

const RelationshipSection = ({ title, relationships, relationshipType, mrnLookupList, showDivider = false }) => (
  <>
    <h4>{title}</h4>
    <div css={justBorder}>
      {relationships.map((rel) => (
        <div key={rel.mrn || `${relationshipType.toLowerCase()}_${rel.name}`}>
          <div css={flexContainer}>
            <div css={relationshipFlexItem}>
              <MrnRelationship
                relative={rel}
                lookupMrn={rel.mrn}
                relationshipType={relationshipType}
                mrnLookupList={mrnLookupList}
              />
            </div>
          </div>
          <Facts factList={rel.relationshipFacts} />
          {showDivider && <Divider />}
        </div>
      ))}
    </div>
  </>
)

const UserRelationships = () => {
  const userContext = useContext(UserContext)
  const currentUser = userContext.currentUser
  const dmcUserMembership = currentUser.dmcUserMembership
  const relationshipList = dmcUserMembership.relationshipList
  const mrnLookupList = dmcUserMembership.mrnLookupList

  const [t] = useTranslation()

  if (!relationshipList) return null

  const parents = relationshipList.filter((rel) => rel.type === 'PARENT')
  const children = relationshipList.filter((rel) => rel.type === 'CHILD')
  const spouses = relationshipList.filter((rel) => rel.type === 'SPOUSE')

  return (
    <>
      <RelationshipSection
        title={t('parents', 'Parents')}
        relationships={parents}
        relationshipType="PARENT"
        mrnLookupList={mrnLookupList}
      />
      <RelationshipSection
        title={t('children', 'Children')}
        relationships={children}
        relationshipType="CHILD"
        mrnLookupList={mrnLookupList}
      />
      <RelationshipSection
        title={t('couple', 'Couple')}
        relationships={spouses}
        relationshipType="SPOUSE"
        mrnLookupList={mrnLookupList}
        showDivider
      />
    </>
  )
}

export default UserRelationships
